<template>
  <article :class="['tm-card', `tm-card--${anzeigeStatus}`, { 'tm-card--favorit': istFavoritSpiel }]" :data-match-id="match.id">
    <div class="tm-card__meta">
      <span class="tm-card__time">{{ formatUhrzeit(match.zeit) }}</span>
      <span v-if="zeigeDatum" class="tm-card__datum">{{ formatKurzDatum(match.zeit) }}</span>
      <span class="tm-card__liga">
        <img v-if="match.ligaLogo" :src="match.ligaLogo" class="tm-card__liga-logo" alt="" />
        <template v-else-if="ligaFlagge(match.ligaLand)">{{ ligaFlagge(match.ligaLand) }} </template>{{ match.liga }}
      </span>
      <span v-if="anzeigeStatus !== 'geplant'" :class="['tm-card__status', `tm-card__status--${anzeigeStatus}`]">
        <span v-if="match.status === 'live'" class="tm-live-dot" aria-hidden="true"></span>
        {{ statusText }}
        <template v-if="match.status === 'live' && spieluhrText">
          {{ ' ' + spieluhrText }}
        </template>
      </span>
    </div>

    <div class="tm-card__main">
      <div class="tm-card__block tm-card__block--teams">
        <div class="tm-team">
          <img v-if="match.heimLogo" :src="match.heimLogo" class="tm-team__logo" alt="" loading="lazy" />
          <span v-else class="tm-team__logo tm-team__logo--platzhalter"></span>
          <span class="tm-team__name">{{ match.heim }}</span>
        </div>
        <div class="tm-team">
          <img v-if="match.auswaertsLogo" :src="match.auswaertsLogo" class="tm-team__logo" alt="" loading="lazy" />
          <span v-else class="tm-team__logo tm-team__logo--platzhalter"></span>
          <span class="tm-team__name">{{ match.auswaerts }}</span>
        </div>
      </div>

      <div v-if="hatQuoten" class="tm-card__block tm-card__block--quoten">
        <div class="tm-quote-zeile">
          <span class="tm-quote-zeile__label">1</span>
          <span :class="['tm-quote-zeile__wert', { 'tm-quote-zeile__wert--favorit': quotenFavorit === '1' }]">{{ match.quote1 ?? '–' }}</span>
        </div>
        <div class="tm-quote-zeile">
          <span class="tm-quote-zeile__label">X</span>
          <span :class="['tm-quote-zeile__wert', { 'tm-quote-zeile__wert--favorit': quotenFavorit === 'X' }]">{{ match.quoteX ?? '–' }}</span>
        </div>
        <div class="tm-quote-zeile">
          <span class="tm-quote-zeile__label">2</span>
          <span :class="['tm-quote-zeile__wert', { 'tm-quote-zeile__wert--favorit': quotenFavorit === '2' }]">{{ match.quote2 ?? '–' }}</span>
        </div>
      </div>

      <div class="tm-card__block tm-card__block--sterne">
        <span>
          <button
            type="button"
            class="tm-favorit-btn"
            :class="{ 'tm-favorit-btn--aktiv': istFavorit(match.heimId) }"
            :aria-label="istFavorit(match.heimId) ? 'Aus Favoriten entfernen' : 'Als Favorit merken'"
            @click="toggleFavorit(match.heimId)"
          >{{ istFavorit(match.heimId) ? '★' : '☆' }}</button>
        </span>
        <span>
          <button
            type="button"
            class="tm-favorit-btn"
            :class="{ 'tm-favorit-btn--aktiv': istFavorit(match.auswaertsId) }"
            :aria-label="istFavorit(match.auswaertsId) ? 'Aus Favoriten entfernen' : 'Als Favorit merken'"
            @click="toggleFavorit(match.auswaertsId)"
          >{{ istFavorit(match.auswaertsId) ? '★' : '☆' }}</button>
        </span>
      </div>

      <div v-if="match.heimtore != null" class="tm-card__block tm-card__block--resultat">
        <span class="tm-team-row__score">{{ match.heimtore }}</span>
        <span class="tm-team-row__score">{{ match.austore }}</span>
      </div>
    </div>

    <div class="tm-card__aktionen">
      <button
        v-if="hatAufstellung"
        type="button"
        class="tm-aktion-btn tm-aktion-btn--aufstellung"
        aria-label="Aufstellung"
        @click="$emit('open-detail', match, 'aufstellung')"
      ><span class="tm-aktion-btn__icon">👕</span><span class="tm-aktion-btn__label">Aufstellung</span></button>
      <button
        v-if="hatStatistik"
        type="button"
        class="tm-aktion-btn tm-aktion-btn--statistik"
        aria-label="Statistik"
        @click="$emit('open-detail', match, 'statistik')"
      ><span class="tm-aktion-btn__icon">📈</span><span class="tm-aktion-btn__label">Statistik</span></button>
      <button
        v-if="hatTabelle"
        type="button"
        class="tm-aktion-btn tm-aktion-btn--tabelle"
        aria-label="Tabelle"
        @click="$emit('open-standings', match)"
      ><span class="tm-aktion-btn__icon">📊</span><span class="tm-aktion-btn__label">Tabelle</span></button>
      <button
        v-if="hatStoryPreview"
        type="button"
        class="tm-aktion-btn"
        aria-label="Vorschau"
        @click="$emit('open-detail', match, 'vorschau')"
      ><span class="tm-aktion-btn__icon">📰</span><span class="tm-aktion-btn__label">Vorschau</span></button>
      <button
        v-if="hatStoryReport"
        type="button"
        :class="['tm-aktion-btn', { 'tm-aktion-btn--vorschau': match.status === 'beendet' }]"
        aria-label="Spielbericht"
        @click="$emit('open-detail', match, 'spielbericht')"
      ><span class="tm-aktion-btn__icon">📝</span><span class="tm-aktion-btn__label">Spielbericht</span></button>

      <div class="tm-card__footer-rechts">
        <img
          v-if="match.ligaFlaggeBild"
          :src="match.ligaFlaggeBild"
          :class="['tm-card__flagge-bild', `tm-card__flagge-bild--${ligaLogoGroesse(match.ligaId)}`]"
          :alt="match.ligaLand ?? ''"
          :title="match.ligaLand"
        />
        <span v-else-if="landesFlagge" :class="['tm-card__flagge', `tm-card__flagge--${ligaLogoGroesse(match.ligaId)}`]" :title="match.ligaLand">{{ landesFlagge }}</span>
        <button type="button" class="tm-teilen-btn" aria-label="Spiel teilen" @click="teilen">
            <span v-if="geradeKopiert">✓</span>
            <svg v-else viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <path fill="currentColor" d="M18 16a3 3 0 0 0-2.4 1.2l-6.7-3.4a3 3 0 0 0 0-3.6l6.7-3.4A3 3 0 1 0 15 5a3 3 0 0 0 .1.7L8.4 9.1a3 3 0 1 0 0 5.8l6.7 3.4A3 3 0 1 0 18 16z"/>
            </svg>
        </button>
      </div>
    </div>
  </article>
</template>

<script setup>
import { ref, computed } from 'vue'
import { formatUhrzeit, formatKurzDatum, formatCountdown, ermittleAnzeigeStatus } from '../lib/format.js'
import { ligaFlagge } from '../lib/flaggen.js'
import { ligaLogoGroesse } from '../lib/ligaTypen.js'
import { tabelleFuerLiga } from '../lib/standings.js'
import { useFavoriten } from '../composables/useFavoriten.js'

const props = defineProps({
  match: { type: Object, required: true },
  jetzt: { type: Number, required: true }, // aktuelle Zeit in ms, fürs Countdown-Ticken
  countdownAbMinuten: { type: Number, default: 120 },
  tabellen: { type: Array, default: () => [] },
  // Nur relevant bei Listen über MEHRERE Tage hinweg (z.B. Team-Suche) - normalerweise weiß man
  // ja schon vom Tages-Header, welcher Tag gerade gezeigt wird, das Datum wäre dort redundant.
  zeigeDatum: { type: Boolean, default: false },
})
defineEmits(['open-detail', 'open-standings'])

const { istFavorit, toggleFavorit } = useFavoriten()

// Zeigt den Countdown auch ohne Server-Bestätigung ('bald'-Status vom Server bleibt weiterhin
// gültig, sobald er ankommt) - rein clientseitig, sobald man nah genug an den Anpfiff kommt.
const anzeigeStatus = computed(() =>
  ermittleAnzeigeStatus(props.match, props.jetzt, props.countdownAbMinuten)
)

const statusText = computed(() => {
  if (anzeigeStatus.value === 'bald') return formatCountdown(props.match.zeit, props.jetzt)
  return { live: 'Live', beendet: 'Beendet', geplant: 'Geplant' }[anzeigeStatus.value] ?? 'Geplant'
})

// Spieluhr-Text neben "Live": "HT" bei Halbzeit (keine laufende Minute), sonst die Minute -
// inkl. Nachspielzeit als "45+6'", falls status.extra > 0 mitgeliefert wird.
const spieluhrText = computed(() => {
  if (props.match.spielUhrKurz === 'HT') return 'HT'
  if (typeof props.match.minute !== 'number' || props.match.minute < 0) return ''
  const extra = props.match.spielUhrExtra
  const extraSuffix = typeof extra === 'number' && extra > 0 ? `+${extra}` : ''
  return `${props.match.minute}${extraSuffix}'`
})

// Bei Live-Spielen zusätzlich zeigen, auch wenn das initiale Flag (aus dem großen Spielplan-
// Request, vor Anpfiff geladen) noch false war - Aufstellung/Statistik werden ja oft erst rund
// um den Anpfiff verfügbar. Ohne das käme der Nutzer nie an den Button, um überhaupt ins
// Detail-Overlay zu gelangen, wo der aktuelle Stand dann live nachgeladen wird.
const hatAufstellung = computed(() => props.match.hatLineup || props.match.status === 'live')
const hatTabelle = computed(() => tabelleFuerLiga(props.tabellen, props.match.ligaId).length > 0)
const hatStoryPreview = computed(() => !!props.match.storyPreview)
const hatStoryReport = computed(() => !!props.match.storyReport)
const hatStatistik = computed(() => props.match.hatStatistik || props.match.status === 'live')
const hatQuoten = computed(() => props.match.quote1 != null || props.match.quoteX != null || props.match.quote2 != null)
const landesFlagge = computed(() => ligaFlagge(props.match.ligaLand))
const istFavoritSpiel = computed(() => istFavorit(props.match.heimId) || istFavorit(props.match.auswaertsId))

// Niedrigste Quote = Wett-Favorit (unwahrscheinlichster Ausgang hat die höchste Quote) - nur
// hervorheben, wenn alle drei Quoten bekannt sind, sonst wäre der Vergleich nicht fair.
const quotenFavorit = computed(() => {
  const { quote1, quoteX, quote2 } = props.match
  if (quote1 == null || quoteX == null || quote2 == null) return null
  const min = Math.min(quote1, quoteX, quote2)
  if (quote1 === min) return '1'
  if (quoteX === min) return 'X'
  return '2'
})

// ---------- Teilen ----------
const geradeKopiert = ref(false)

// Ermittelt die Basis-URL für den Teilen-Link:
// - Läuft die Seite selbst auf app.team-match.io (z.B. /tippen/friesacher/spielplan), wird
//   genau DIESER Pfad übernommen - jede Company/jeder Veranstalter landet so beim eigenen
//   Auftritt, nicht beim Hersteller-Standard.
// - Läuft die Seite auf einer FREMDEN Domain (z.B. eine Partner-Website, die das Widget nur
//   einbettet), lässt sich die Company nicht zuverlässig aus der URL ableiten - dort fester
//   Fallback auf die eigene Standard-Tipp-App des Herstellers.
function ermittleTeilenBasisUrl() {
  if (window.location.hostname === 'app.team-match.io') {
    return `${window.location.origin}${window.location.pathname}`
  }
  return 'https://app.team-match.io/tippen/ike/spielplan'
}

async function teilen() {
  const url = new URL(ermittleTeilenBasisUrl())
  url.searchParams.set('tm-match', props.match.id)
  const link = url.toString()
  const titel = `${props.match.heim} – ${props.match.auswaerts}`

  if (navigator.share) {
    try {
      await navigator.share({ title: titel, url: link })
    } catch (e) {
      // Nutzer hat den Share-Dialog abgebrochen -> kein echter Fehler, ignorieren
    }
    return
  }

  // Clipboard-API braucht einen "sicheren Kontext" (HTTPS oder localhost) - fehlt der, ist
  // navigator.clipboard schlicht undefined. Dann greift der execCommand-Fallback weiter unten.
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(link)
      geradeKopiert.value = true
      setTimeout(() => { geradeKopiert.value = false }, 1500)
      return
    } catch (e) {
      console.warn('Clipboard-API fehlgeschlagen, versuche Fallback:', e)
    }
  }

  // Fallback für unsichere Kontexte (kein HTTPS) oder ältere Browser ohne Clipboard-API
  try {
    const textarea = document.createElement('textarea')
    textarea.value = link
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
    geradeKopiert.value = true
    setTimeout(() => { geradeKopiert.value = false }, 1500)
  } catch (e) {
    console.warn('Teilen/Kopieren komplett fehlgeschlagen:', e)
  }
}
</script>